import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { rooms } from '../data/rooms'
import { services, BUSINESS } from '../data/services'
import ServiceCard from '../components/ServiceCard'
import GoogleMapEmbed from '../components/GoogleMapEmbed'
import SeoHead, { SITE_URL } from '../components/SeoHead'

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: BUSINESS.name,
  url: SITE_URL,
  telephone: BUSINESS.telephone,
  email: BUSINESS.email,
  address: BUSINESS.address,
  sameAs: BUSINESS.sameAs,
  description: 'Professional office space, virtual business addresses, and meeting room rentals in El Cajon, CA.',
  priceRange: '$$',
  // Front-desk / walk-in hours — building access itself is 24/7 for clients and members, but
  // that's not something OpeningHoursSpecification is meant to represent (it's "when is someone
  // here to help a general visitor," not "when can an authorized client badge in").
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    opens: '09:00',
    closes: '12:00',
  },
}

function CheckIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  )
}

const aboutInBrief = [
  {
    title: '40+ Years in Business',
    desc: 'ECBC has served East County entrepreneurs and small business owners for over 40 years.',
  },
  {
    title: 'Hundreds of Clients Served',
    desc: "From solo freelancers to growing teams, we've helped hundreds of businesses establish a professional presence.",
  },
  {
    title: 'No Long-Term Commitments',
    desc: "Month-to-month agreements on everything. If it's not working for you, we won't hold you to it.",
  },
]

export default function Home() {
  const featuredServices = services.filter(s => s.homeFeatured)
  const orderedFeaturedServices = [
    ...featuredServices.filter(s => s.id === 'virtual-address'),
    ...featuredServices.filter(s => s.id !== 'virtual-address'),
  ]
  const virtualAddressHomeDesc = 'A real San Diego-area street address — no mailbox to rent, no office to lease. Built for remote workers, home-based businesses, and out-of-area companies who just need a credible local presence.'

  return (
    <>
      <SeoHead
        title="East County Business Center — El Cajon Office Space"
        description="East County Business Center offers professional office space, virtual business addresses, and meeting room rentals in El Cajon, CA. Flexible plans for every business."
        path="/"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      </Helmet>
      {/* ── Hero ── */}
      <section
        className="relative bg-ecbc-navy text-white py-24 px-6 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: "url('/images/hero-front.webp')" }}
      >
        <div className="absolute inset-0 bg-ecbc-navy/55" />
        <div className="relative max-w-4xl mx-auto text-center">
          <p className="text-ecbc-sky font-medium text-sm uppercase tracking-widest mb-5">
            A Real East County Business Address
          </p>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
            Your Business Deserves a<br />
            <span className="text-ecbc-sky">Professional Space</span>
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-2xl mx-auto mb-6 leading-relaxed">
            Professional business addresses, private offices, and meeting spaces in the heart of El Cajon.
          </p>
          <h2 className="text-xl md:text-2xl font-bold mb-2">
            <Link to="/services/virtual-address" className="text-ecbc-sky hover:underline">
              Virtual Business Address
            </Link>
          </h2>
          <p className="text-base text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
            Don't need office space yet? Start here — a real street address at our 270 E Douglas Ave building, not a PO box.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/services"
              className="bg-ecbc-sky text-ecbc-navy font-bold px-8 py-3.5 rounded-md hover:bg-white transition-colors duration-200"
            >
              Explore Our Services
            </Link>
            <Link
              to="/booking"
              className="inline-flex items-center justify-center gap-2 bg-white text-ecbc-navy font-bold px-8 py-3.5 rounded-md shadow-lg ring-2 ring-white/40 ring-offset-2 ring-offset-transparent hover:bg-ecbc-sky transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Book a Room
            </Link>
          </div>
        </div>
      </section>

      {/* ── About in Brief ── */}
      <section className="bg-white border-b border-gray-100 py-16 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8">
          {aboutInBrief.map(({ title, desc }) => (
            <div key={title} className="text-center">
              <h3 className="text-xl font-bold text-ecbc-navy mb-2">{title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Services overview ── */}
      <section className="bg-ecbc-gray py-20 px-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ecbc-navy mb-3">Everything Your Business Needs</h2>
            <p className="text-gray-600 max-w-xl mx-auto">
              Whether you need a professional address, a private office, or a meeting space, we have a solution for you.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {orderedFeaturedServices.map((service) => {
              const isVirtualAddress = service.id === 'virtual-address'
              return (
                <ServiceCard
                  key={service.id}
                  icon={service.icon}
                  title={service.title}
                  price={service.price}
                  priceNote={service.priceNote}
                  description={isVirtualAddress ? virtualAddressHomeDesc : service.shortDesc}
                  to={`/services/${service.id}`}
                  highlight={isVirtualAddress}
                  badge={isVirtualAddress ? 'Most Flexible' : undefined}
                />
              )
            })}
          </div>
        </div>
      </section>

      {/* ── Room highlights ── */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ecbc-navy mb-3">Our Spaces</h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              Three professional spaces designed to help you and your team do your best work.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {rooms.map((room) => (
              <div
                key={room.id}
                className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md hover:border-ecbc-navy/30 transition-all duration-200 flex flex-col"
              >
                {room.images?.length > 0 && (
                  <img src={room.images[0]} alt={room.name} className="w-full h-40 object-cover object-[center_70%]" />
                )}
                <div className="p-7 flex flex-col flex-1">
                <div className="flex items-start justify-between mb-4">
                  <span className="bg-ecbc-sky/25 text-ecbc-navy text-xs font-semibold px-2.5 py-1 rounded-full">
                    {room.type}
                  </span>
                  <span className="text-xs text-gray-500 font-medium">Seats {room.capacity}</span>
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{room.name}</h3>
                <p className="text-gray-500 text-sm mb-5 leading-relaxed">{room.description}</p>
                <ul className="space-y-2 mb-6">
                  {room.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="text-ecbc-navy">
                        <CheckIcon />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/booking?room=${room.id}`}
                  className="block text-center bg-ecbc-navy text-white text-sm font-semibold py-2.5 px-4 rounded-lg hover:bg-ecbc-navy/90 transition-colors duration-200 mt-auto"
                >
                  Book Now
                </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Location / contact strip ── */}
      <section className="bg-ecbc-gray border-y border-gray-100 py-16 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="aspect-video rounded-xl overflow-hidden bg-white border border-gray-200">
            <GoogleMapEmbed className="w-full h-full" />
          </div>
          <div className="flex flex-col gap-3 text-sm text-gray-600">
            <h2 className="text-2xl font-bold text-ecbc-navy mb-1">Visit Us in El Cajon</h2>
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-ecbc-navy mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>270 E Douglas Ave<br />El Cajon, CA 92020</span>
            </div>
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-ecbc-navy flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 7V5z" />
              </svg>
              <a href="tel:6194014000" className="hover:text-ecbc-navy transition-colors font-medium">(619) 401-4000</a>
            </div>
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-ecbc-navy flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <a href="mailto:info@eastcountybiz.com" className="hover:text-ecbc-navy transition-colors font-medium">info@eastcountybiz.com</a>
            </div>
            <p className="text-gray-600 mt-1">
              Front desk: Mon–Fri 9am–12pm · 24/7 building access for clients
            </p>
          </div>
        </div>
      </section>

      {/* ── CTA banner ── */}
      <section className="bg-ecbc-navy py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Reserve Your Space?</h2>
          <p className="text-white/65 text-lg mb-8">
            Check availability and book online, or give us a call — we're happy to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/booking"
              className="bg-ecbc-sky text-ecbc-navy font-bold px-8 py-3.5 rounded-md hover:bg-white transition-colors duration-200"
            >
              View Available Rooms
            </Link>
            <a
              href="tel:6194014000"
              className="text-white border-2 border-white/30 font-semibold px-8 py-3.5 rounded-md hover:border-ecbc-sky hover:text-ecbc-sky transition-colors duration-200"
            >
              Call: (619) 401-4000
            </a>
          </div>
        </div>
      </section>
    </>
  )
}
